import { lazy, Suspense } from 'react';
import { Box } from '@mui/material';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { UserRole } from 'shared';
import { PublicLayout, AdminLayout, CompanyLayout } from './components/layouts';
import { ProtectedRoute } from './components/auth';
import { AuthProvider, SocketProvider } from './contexts';
import { ErrorBoundary } from './components/ErrorBoundary';

// Lazy-loaded public pages
const LoginPage = lazy(() => import('./pages/public/LoginPage'));
const RegisterPage = lazy(() => import('./pages/public/RegisterPage'));
const PlansPage = lazy(() => import('./pages/public/PlansPage'));
const VerifyEmailPage = lazy(() => import('./pages/public/VerifyEmailPage'));
const SetupPasswordPage = lazy(() => import('./pages/public/SetupPasswordPage'));

// Lazy-loaded admin pages
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const CompanyManagement = lazy(() => import('./pages/admin/CompanyManagement'));
const PlanManagement = lazy(() => import('./pages/admin/PlanManagement'));
const BillingOverview = lazy(() => import('./pages/admin/BillingOverview'));
const AuditLog = lazy(() => import('./pages/admin/AuditLog'));
const JobQueue = lazy(() => import('./pages/admin/JobQueue'));
const ApiKeysPage = lazy(() => import('./pages/admin/ApiKeysPage'));

// Lazy-loaded dashboard pages
const DashboardOverview = lazy(() => import('./pages/dashboard/DashboardOverview'));
const ServerTabsContainer = lazy(() => import('./pages/dashboard/ServerTabsContainer'));
const AlertsPage = lazy(() => import('./pages/dashboard/AlertsPage'));
const AWSMonitoringView = lazy(() => import('./pages/dashboard/AWSPanel'));
const GitHubIntegrationView = lazy(() => import('./pages/dashboard/GitHubIntegrationView'));
const AIChatInterface = lazy(() => import('./pages/dashboard/AIChatInterface'));
const BillingDashboard = lazy(() => import('./pages/dashboard/BillingDashboard'));
const SettingsPage = lazy(() => import('./pages/dashboard/SettingsPage'));
const UsersPage = lazy(() => import('./pages/dashboard/UsersPage'));
const AgentPage = lazy(() => import('./pages/dashboard/AgentPage'));
const DocsPage = lazy(() => import('./pages/dashboard/DocsPage'));

// Loading fallback component
function PageLoader() {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', maxWidth: 600, px: 4 }}>
        <Box sx={{ height: 32, borderRadius: 2, bgcolor: 'var(--bg-2, rgba(255,255,255,0.05))', animation: 'pulse 1.5s ease-in-out infinite' }} />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box sx={{ height: 80, flex: 1, borderRadius: 2, bgcolor: 'var(--bg-2, rgba(255,255,255,0.05))', animation: 'pulse 1.5s ease-in-out 0.2s infinite' }} />
          <Box sx={{ height: 80, flex: 1, borderRadius: 2, bgcolor: 'var(--bg-2, rgba(255,255,255,0.05))', animation: 'pulse 1.5s ease-in-out 0.4s infinite' }} />
          <Box sx={{ height: 80, flex: 1, borderRadius: 2, bgcolor: 'var(--bg-2, rgba(255,255,255,0.05))', animation: 'pulse 1.5s ease-in-out 0.6s infinite' }} />
        </Box>
        <Box sx={{ height: 200, borderRadius: 2, bgcolor: 'var(--bg-2, rgba(255,255,255,0.05))', animation: 'pulse 1.5s ease-in-out 0.3s infinite' }} />
      </Box>
      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
    </Box>
  );
}

function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <SocketProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public pages */}
          {/* Login — full-screen, no header/footer */}
          <Route path="/login" element={<LoginPage />} />

          <Route element={<PublicLayout />}>
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/plans" element={<PlansPage />} />
            <Route
              path="/register"
              element={<RegisterPage />}
            />
            <Route
              path="/register/verify"
              element={<VerifyEmailPage />}
            />
            <Route path="/setup-password" element={<SetupPasswordPage />} />
          </Route>

          {/* Admin dashboard — protected, admin role only */}
          <Route
            element={<ProtectedRoute allowedRoles={[UserRole.Admin]} />}
          >
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route
                path="companies"
                element={<CompanyManagement />}
              />
              <Route
                path="plans"
                element={<PlanManagement />}
              />
              <Route
                path="billing"
                element={<BillingOverview />}
              />
              <Route path="audit" element={<AuditLog />} />
              <Route path="api-keys" element={<ApiKeysPage />} />
              <Route path="jobs" element={<JobQueue />} />
            </Route>
          </Route>

          {/* Company dashboard — protected, company roles only */}
          <Route
            element={
              <ProtectedRoute
                allowedRoles={[
                  UserRole.CompanyAdmin,
                  UserRole.CompanyUser,
                  UserRole.CompanyViewer,
                ]}
              />
            }
          >
            <Route path="/dashboard" element={<CompanyLayout />}>
              <Route index element={<DashboardOverview />} />
              <Route path="servers" element={<ServerTabsContainer />} />
              <Route path="alerts" element={<AlertsPage />} />
              <Route path="aws" element={<AWSMonitoringView />} />
              <Route
                path="github"
                element={<GitHubIntegrationView />}
              />
              <Route
                path="ai"
                element={<AIChatInterface />}
              />
              <Route path="billing" element={<BillingDashboard />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="agent" element={<AgentPage />} />
              <Route path="docs" element={<DocsPage />} />
              <Route path="settings" element={<SettingsPage />} />
            </Route>
          </Route>

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
      </SocketProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
